import { router } from '@inertiajs/react'
import { ArrowLeftIcon, UserIcon, SendIcon, MessageCircleIcon } from 'lucide-react'
import { format } from 'date-fns'
import { useState, useEffect, useRef } from 'react'
import * as ActionCable from '@rails/actioncable'

import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface Lead {
  id: number
  name: string
}

interface WhatsappConnection {
  id: number
  phone_number: string
  verified_name: string
  business_type: string
}

interface Conversation {
  id: number
  customer_phone_number: string
  customer_name: string | null
  lead: Lead
  whatsapp_connection: WhatsappConnection
}

interface Message {
  id: number
  wa_message_id: string
  content: string
  direction: 'inbound' | 'outbound'
  status: string
  sent_at: string
}

interface MessageShowProps {
  conversation: Conversation
  messages: Message[]
}

export default function MessageShow({ conversation, messages: initialMessages }: MessageShowProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [messageText, setMessageText] = useState('')
  const [isSending, setIsSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const cableRef = useRef<ActionCable.Cable | null>(null)
  const channelRef = useRef<ActionCable.Channel | null>(null)

  const handleBack = () => {
    router.visit('/whatsapp/recruiting/messages')
  }

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Set up ActionCable connection
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.error('[ActionCable] No auth token found')
      return
    }

    // Create cable connection
    const cable = ActionCable.createConsumer(`/cable?token=${token}`)
    cableRef.current = cable

    // Subscribe to WhatsApp conversation channel
    const channel = cable.subscriptions.create(
      {
        channel: 'WhatsAppConversationChannel',
        conversation_id: conversation.id,
      },
      {
        connected() {
          console.log('[ActionCable] Connected to WhatsApp conversation', conversation.id)
        },
        disconnected() {
          console.log('[ActionCable] Disconnected from WhatsApp conversation', conversation.id)
        },
        received(data: { type: string; message: Message }) {
          console.log('[ActionCable] Received data:', data)
          if (data.type === 'new_message') {
            setMessages((prev) => [...prev, data.message])
          }
        },
      }
    )
    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
      if (cableRef.current) {
        cableRef.current.disconnect()
      }
    }
  }, [conversation.id])

  const handleSendMessage = async () => {
    if (!messageText.trim() || isSending) return

    setIsSending(true)

    try {
      // Use direct fetch instead of Inertia router to avoid modal popup
      const token = localStorage.getItem('auth_token')
      const response = await fetch(`/whatsapp/recruiting/messages/${conversation.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message_text: messageText.trim() }),
      })

      if (response.ok) {
        setMessageText('')
        // Message will appear via ActionCable broadcast
      } else {
        console.error('[SendMessage] Failed:', response.status, response.statusText)
      }
    } catch (error) {
      console.error('[SendMessage] Error:', error)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatMessageTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), 'MMM d, yyyy h:mm a')
    } catch {
      return ''
    }
  }

  const displayName = conversation.customer_name || conversation.customer_phone_number

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col min-w-0">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 border-b bg-background px-4 py-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              data-testid="back-button"
            >
              <ArrowLeftIcon className="size-5" />
            </Button>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar className="size-10">
                <AvatarFallback className="bg-[#25D366] text-white">
                  {getInitials(displayName)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold truncate">{displayName}</h1>
                  <Badge variant="outline" className="bg-orange-100 text-orange-700">
                    Driver Lead
                  </Badge>
                </div>
                {conversation.customer_name && (
                  <p className="text-sm text-muted-foreground truncate">
                    {conversation.customer_phone_number}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <MessageCircleIcon className="size-4" />
            <span>Via {conversation.whatsapp_connection.verified_name} ({conversation.whatsapp_connection.phone_number})</span>
            <Badge variant="outline" className="ml-2 bg-orange-100 text-orange-700">
              Recruiting
            </Badge>
          </div>
        </div>

        {/* Messages Container - Scrollable with fixed height */}
        <div className="flex-1 overflow-y-auto px-4 lg:px-6 min-w-0" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <div className="py-6">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center text-muted-foreground">
                  <MessageCircleIcon className="mx-auto mb-4 size-12 text-muted-foreground" />
                  <p>No messages in this conversation yet</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const isOutbound = message.direction === 'outbound'

                  return (
                    <div
                      key={message.id}
                      className={cn(
                        'flex',
                        isOutbound ? 'justify-end' : 'justify-start'
                      )}
                      data-testid={`message-${message.id}`}
                    >
                      <div
                        className={cn(
                          'max-w-[70%] rounded-lg px-4 py-2',
                          isOutbound
                            ? 'bg-[#25D366] text-white'
                            : 'bg-muted text-foreground'
                        )}
                      >
                        <p className="whitespace-pre-wrap break-words">
                          {message.content}
                        </p>
                        <p
                          className={cn(
                            'mt-1 text-xs',
                            isOutbound ? 'text-green-100' : 'text-muted-foreground'
                          )}
                        >
                          {formatMessageTime(message.sent_at)}
                          {isOutbound && message.status && (
                            <span className="ml-2 capitalize">• {message.status}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Message Input - Fixed at bottom */}
        <div className="flex-shrink-0 border-t bg-background px-4 py-4 lg:px-6">
          <div className="flex gap-3">
            <Textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message to the driver candidate..."
              className="min-h-[80px] resize-none"
              disabled={isSending}
              data-testid="message-input"
            />
<Button
              onClick={handleSendMessage}
              disabled={!messageText.trim() || isSending}
              size="icon"
              className="bg-[#25D366] hover:bg-[#1da851] text-white h-[80px] w-[80px] shrink-0 shadow-lg shadow-[#25D366]/20"
              data-testid="send-button"
            >
              <SendIcon className="size-5" />
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  )
}
